import React, { useState, useEffect } from "react";
import {
  Send,
  Mail,
  User,
  Phone,
  ChevronDown,
  MessageSquare,
  X,
  Check,
} from "lucide-react";
import { useNavigate } from 'react-router-dom';
import form from "../img/form.png";
import { parsePhoneNumberFromString } from "libphonenumber-js";

const QuoteForm = () => {
  const navigate = useNavigate();
  const [selectedCountry, setSelectedCountry] = useState("IN");

  // Google Tag Manager and Meta Pixel tracking on mount
  useEffect(() => {
    // Google Tag Manager tracking
    if (typeof window.dataLayer !== 'undefined') {
      window.dataLayer.push({
        event: 'page_view',
        page_title: 'Home Quote Form',
        page_location: window.location.href,
      });
    }
    
    // Meta Pixel tracking
    if (typeof window.fbq !== 'undefined') {
      window.fbq('track', 'PageView', {
        page_title: 'Home Quote Form'
      });
    }
  }, []);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phoneno: "",
    service: "",
    message: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const countryCodes = {
    IN: "+91",
    US: "+1",
    GB: "+44",
    AU: "+61",
    CA: "+1",
    AE: "+971",
    SG: "+65",
    DE: "+49",
    FR: "+33",
    SA: "+966",
  };

  /* ================= VALIDATION ================= */
  const nameRegex = /^[A-Za-z\s]+$/;

  const isValidPhoneNumber = (number, countryCode) => {
    try {
      const phoneNumber = parsePhoneNumberFromString(number, countryCode);
      return phoneNumber ? phoneNumber.isValid() : false;
    } catch {
      return false;
    }
  };

  /* ================= INPUT HANDLER ================= */
  const handleChange = (e) => {
    const { name, value } = e.target;

    // Name → alphabets only
    if (name === "name") {
      if (!/^[A-Za-z\s]*$/.test(value)) return;
    }

    // Phone → digits only, max 10
    if (name === "phoneno") {
      if (!/^[0-9]*$/.test(value)) return;
      if (value.length > 10) return;
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  /* ================= SUBMIT ================= */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Name validation
    if (!nameRegex.test(formData.name)) {
      setIsSubmitting(false);
      return;
    }

    // Phone validation
    const validPhone = isValidPhoneNumber(
      formData.phoneno,
      selectedCountry
    );

    if (!validPhone || formData.phoneno.length !== 10) {
      setIsSubmitting(false);
      return;
    }

    const payload = {
      name: formData.name,
      email: formData.email,
      country_code: countryCodes[selectedCountry] || "",
      phoneno: formData.phoneno,
      service: formData.service,
      message: formData.message,
    };

    try {
      const res = await fetch(
        "https://api.securxperts.com:8000/forms/quotation",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );

      if (!res.ok) throw new Error("Failed");

      // Redirect to thank you page on success
      navigate('/thank-you', { state: { formType: 'home-quote' } });

      setFormData({
        name: "",
        email: "",
        phoneno: "",
        service: "",
        message: "",
      });
      setSelectedCountry("IN");

    } catch {
      setIsSubmitting(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="bg-gray-50 py-16 px-6 lg:px-12 relative">
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-10 items-center mb-[-4rem]">
        {/* FORM */}
        <div>
          <h5 className="text-blue-600 font-semibold text-sm uppercase flex items-center gap-2">
            <MessageSquare className="h-4 w-4" />
            Request a Quote
          </h5>

          <h2 className="text-4xl font-bold mt-2 mb-4">
            Have Any <span className="text-blue-600">Questions?</span>
          </h2>

          <form className="space-y-4" onSubmit={handleSubmit}>
            {/* Name + Email */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="relative">
                {/* <User className="absolute top-3 right-3 text-gray-400 h-5 w-5" /> */}
                <input
                  type="text"
                  name="name"
                  placeholder="Full Name*"
                  maxLength={60}
                  className="w-full border rounded-lg px-4 py-3"
                  value={formData.name}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="relative">
                {/* <Mail className="absolute top-3 right-3 text-gray-400 h-5 w-5" /> */}
                <input
                  type="email"
                  name="email"
                  placeholder="Email Address*"
                  maxLength={80}
                  className="w-full border rounded-lg px-4 py-3"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  pattern="^[a-zA-Z0-9][a-zA-Z0-9._%+-]*@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$"
                  title="Email must start with letter/number, have exactly one @, and valid domain"
                />
              </div>

            </div>

            {/* Country + Phone */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <select
                className="border rounded-lg px-4 py-3"
                value={selectedCountry}
                onChange={(e) => setSelectedCountry(e.target.value)}
              >
                {Object.entries(countryCodes).map(([c, d]) => (
                  <option key={c} value={c}>
                    {c} ({d})
                  </option>
                ))}
              </select>

              <input
                type="tel"
                name="phoneno"
                placeholder="Phone Number*"
                className="border rounded-lg px-4 py-3"
                value={formData.phoneno}
                onChange={handleChange}
                maxLength={10}
                required
              />
            </div>

            <select
              name="service"
              className="border rounded-lg px-4 py-3 w-full"
              value={formData.service}
              onChange={handleChange}
              required
            >
              <option value="">Select Service</option>
              <option>Web Development</option>
              <option>Mobile App Development</option>
              <option>UI/UX Design</option>
              <option>Digital Marketing</option>
            </select>

            <textarea
              name="message"
              rows="4"
              placeholder="Write your message..."
              className="border rounded-lg px-4 py-3 w-full"
              value={formData.message}
              onChange={handleChange}
            />

            <button
              type="submit"
              disabled={isSubmitting}
              className="bg-blue-600 text-white px-6 py-3 rounded-lg w-full md:w-auto hover:bg-blue-700 font-medium transition disabled:opacity-50 flex items-center justify-center"
            >
              {isSubmitting ? "Submitting..." : "Submit"}
              <Send className="inline ml-2 h-4 w-4" />
            </button>
          </form>
        </div>

        {/* IMAGE */}
        {/* <div className="flex justify-center">
          <img src={form} alt="Quote" className="max-w-lg" />
        </div> */}
        <div className="flex justify-center px-4">
          <img
            src={form}
            alt="Quote"
            className="w-full max-w-lg h-auto"
          />
        </div>

      </div>

    </section>
  );
};

export default QuoteForm;
